import { z } from 'zod';
import {
  MAX_TOPOLOGY_DEPTH,
  SPLITTER_CAPACITIES,
  SPLITTER_DEPTH_MESSAGE,
  SPLITTER_KIND_LABELS,
  SPLITTER_KIND_ORDER_MESSAGE,
  SPLITTER_KIND_RANK,
  SPLITTER_LOSS_AMBER_DB,
  fibreLossDb,
  splitterKindSchema,
  splitterLossDb,
  type SplitterKind,
} from './topology.js';

/**
 * The cascade wizard's plan: level specs in, the exact splitters to create
 * out — names, parents, ports and accumulated loss — plus every refusal, in
 * the model's own words.
 *
 * This is PURE on purpose. The wizard computes it live on every keystroke for
 * the per-level loss readout and the review table, and the API applies the
 * same expansion, so what the review step shows is what apply writes. The
 * loss figures come from `splitterLossDb` — the signal diagram's own table —
 * and amber at `SPLITTER_LOSS_AMBER_DB`, so the wizard and the diagram can
 * never disagree about a cascade they are both describing.
 */

/** The most splitters one run may create — a plan past this is two runs. */
export const CASCADE_MAX_NODES = 64;

export interface CascadeLevelSpec {
  kind: SplitterKind;
  /** One of SPLITTER_CAPACITIES. */
  capacity: number;
  /** Children hung on EACH parent from the level above (on the anchor for level 1). */
  countPerParent: number;
  /** `{n}` is the running number within the level; a pattern without it gets `-{n}` appended when the level makes more than one. */
  namePattern: string;
  /**
   * Planned fibre from the level above to this one, metres (2026-09-16).
   * Optional and additive: absent, null or 0 leaves the figures exactly as
   * they were; a length adds `fibreLossDb` of it to this level and every
   * level below.
   */
  fibreLengthM?: number | null;
}

export type CascadePlanAnchor =
  | { type: 'port'; oltId: string; ponPort: string; oltName?: string }
  | {
      type: 'splitter';
      id: string;
      name: string;
      kind: SplitterKind;
      capacity: number;
      /** Ancestor count — 0 for a main splitter. */
      depth: number;
      /** Outputs already spoken for: sub-splitters plus placed ONUs. */
      occupied: number;
      /** Accumulated splitter loss down to and including the anchor, dB. */
      lossDb: number;
      /**
       * Measured fibre from the top of the chain down to the anchor, metres —
       * the splitter's `feedLengthM` from GET /topology when its routes are
       * drawn (2026-09-16). Its `fibreLossDb` joins the base every level
       * starts from; absent or null adds nothing.
       */
      feedLengthM?: number | null;
    };

export interface CascadePlanNode {
  /** Position in creation order; a parent always precedes its children. */
  index: number;
  /** 1-based level in the composed chain. */
  level: number;
  name: string;
  kind: SplitterKind;
  capacity: number;
  /** Index of the parent within the plan; null = hangs on the anchor. */
  parentIndex: number | null;
  /** What the review table prints in "hangs on": the parent's name, or the anchor. */
  parentLabel: string;
  /** Set only on nodes attaching straight to an OLT PON port. */
  oltId: string | null;
  oltPonPort: string | null;
  /** Accumulated splitter loss down to and including this node, dB. */
  lossDb: number;
  amber: boolean;
}

export interface CascadePlanLevel {
  level: number;
  kind: SplitterKind;
  capacity: number;
  /** Splitters this level creates in total. */
  count: number;
  /** Accumulated loss through this level, dB — splitters plus whatever fibre is known. */
  lossDb: number;
  /** This level's own fibre term, dB (0 when no length was given). */
  fibreLossDb: number;
  amber: boolean;
}

export interface CascadePlan {
  /** Empty when a problem stops expansion (over the cap) or there are no levels. */
  nodes: CascadePlanNode[];
  levels: CascadePlanLevel[];
  total: number;
  /** Refusals, in the model's own words. Empty = buildable. */
  problems: string[];
}

/** `FAT-{n}` → `FAT-3`; a pattern without `{n}` gets `-{n}` when it must number. */
export function cascadeName(pattern: string, n: number, siblings: number): string {
  const trimmed = pattern.trim();
  if (trimmed.includes('{n}')) return trimmed.replaceAll('{n}', String(n)).trim();
  return siblings > 1 ? `${trimmed}-${n}` : trimmed;
}

const label = (kind: SplitterKind) => SPLITTER_KIND_LABELS[kind];

export function planCascade(
  anchor: CascadePlanAnchor,
  levels: CascadeLevelSpec[],
): CascadePlan {
  const problems: string[] = [];
  const push = (p: string) => {
    if (!problems.includes(p)) problems.push(p);
  };

  if (levels.length === 0) return { nodes: [], levels: [], total: 0, problems: [] };

  // --- shape problems, judged before any expansion ---------------------------
  let previousRank = anchor.type === 'splitter' ? SPLITTER_KIND_RANK[anchor.kind] : -1;
  for (const level of levels) {
    if (SPLITTER_KIND_RANK[level.kind] < previousRank) push(SPLITTER_KIND_ORDER_MESSAGE);
    previousRank = Math.max(previousRank, SPLITTER_KIND_RANK[level.kind]);
    if (!(SPLITTER_CAPACITIES as readonly number[]).includes(level.capacity)) {
      push(`Capacity must be one of ${SPLITTER_CAPACITIES.join(', ')} outputs`);
    }
    if (!Number.isInteger(level.countPerParent) || level.countPerParent < 1) {
      push('Every level fans out to at least one splitter per parent.');
    }
    if (cascadeName(level.namePattern, 1, 1) === '') {
      push('Every level needs a name pattern.');
    }
  }

  // Depth: the deepest level's ancestor count may not pass the model's limit.
  const baseDepth = anchor.type === 'splitter' ? anchor.depth + 1 : 0;
  if (baseDepth + levels.length - 1 > MAX_TOPOLOGY_DEPTH) push(SPLITTER_DEPTH_MESSAGE);

  // Fill: the anchor's free outputs, counting who already sits on them; below
  // that, each level's fan-out against the outputs of the level feeding it.
  const rootCount = levels[0]!.countPerParent;
  if (anchor.type === 'splitter' && Number.isInteger(rootCount) && rootCount >= 1) {
    const free = Math.max(0, anchor.capacity - anchor.occupied);
    if (rootCount > free) {
      push(
        `${anchor.name} has ${free} free output(s) of ${anchor.capacity} — cannot hang ${rootCount} more splitter(s)`,
      );
    }
  }
  for (let i = 1; i < levels.length; i += 1) {
    const feed = levels[i - 1]!;
    const fan = levels[i]!.countPerParent;
    if (Number.isInteger(fan) && fan > feed.capacity) {
      push(
        `A 1×${feed.capacity} ${label(feed.kind)} has ${feed.capacity} output(s) — it cannot fan out to ${fan} splitter(s) each`,
      );
    }
  }

  // --- per-level counts and loss ---------------------------------------------
  // The fibre already measured down to the anchor joins the base (2026-09-16);
  // with no routes drawn it is 0 and the figures are the splitters' alone.
  const baseLoss =
    anchor.type === 'splitter'
      ? Math.round((anchor.lossDb + fibreLossDb(anchor.feedLengthM)) * 10) / 10
      : 0;
  const planLevels: CascadePlanLevel[] = [];
  let parentsAbove = 1;
  let runningLoss = baseLoss;
  let total = 0;
  for (const [i, level] of levels.entries()) {
    const perParent = Math.max(1, Math.floor(level.countPerParent) || 1);
    const count = parentsAbove * perParent;
    const fibre = fibreLossDb(level.fibreLengthM);
    runningLoss = Math.round((runningLoss + splitterLossDb(level.capacity) + fibre) * 10) / 10;
    planLevels.push({
      level: i + 1,
      kind: level.kind,
      capacity: level.capacity,
      count,
      lossDb: runningLoss,
      fibreLossDb: fibre,
      amber: runningLoss > SPLITTER_LOSS_AMBER_DB,
    });
    parentsAbove = count;
    total += count;
  }

  if (total > CASCADE_MAX_NODES) {
    push(
      `A cascade run caps at ${CASCADE_MAX_NODES} splitters so the review stays reviewable — this plan asks for ${total}. Split it into more than one run.`,
    );
  }

  // --- node expansion, parents first -----------------------------------------
  // Skipped past the cap: a runaway fan-out must not ask the review table to
  // render thousands of rows.
  const nodes: CascadePlanNode[] = [];
  if (total <= CASCADE_MAX_NODES) {
    const anchorLabel =
      anchor.type === 'port'
        ? `${anchor.oltName ?? 'OLT'} · ${anchor.ponPort}`
        : anchor.name;
    let previousLevelStart = 0;
    for (const [i, level] of levels.entries()) {
      const perParent = Math.max(1, Math.floor(level.countPerParent) || 1);
      const count = planLevels[i]!.count;
      const levelStart = nodes.length;
      for (let n = 0; n < count; n += 1) {
        const parentIndex =
          i === 0 ? null : previousLevelStart + Math.floor(n / perParent);
        nodes.push({
          index: nodes.length,
          level: i + 1,
          name: cascadeName(level.namePattern, n + 1, count),
          kind: level.kind,
          capacity: level.capacity,
          parentIndex,
          parentLabel: parentIndex === null ? anchorLabel : nodes[parentIndex]!.name,
          oltId: i === 0 && anchor.type === 'port' ? anchor.oltId : null,
          oltPonPort: i === 0 && anchor.type === 'port' ? anchor.ponPort : null,
          lossDb: planLevels[i]!.lossDb,
          amber: planLevels[i]!.amber,
        });
      }
      previousLevelStart = levelStart;
    }
    for (const node of nodes) {
      if (node.name.length > 120) {
        push(`"${node.name}" runs past 120 characters — shorten the pattern.`);
        break;
      }
    }
  }

  return { nodes, levels: planLevels, total, problems };
}

// --- the apply call's body ----------------------------------------------------

/**
 * What POST /api/splitters/cascade takes: the EXPANDED plan, exactly as the
 * review step showed it, so the server writes what the operator read and
 * nothing recomputed behind their back. The service re-judges every guardrail
 * against the database before writing.
 */
export const cascadeChainSchema = z.object({
  zoneId: z.string().nullable().optional(),
  anchor: z.union([
    z.object({ oltId: z.string().min(1), ponPort: z.string().min(1).max(64) }),
    z.object({ splitterId: z.string().min(1) }),
  ]),
  nodes: z
    .array(
      z.object({
        name: z.string().min(1).max(120),
        kind: splitterKindSchema,
        capacity: z.coerce
          .number()
          .int()
          .refine((v) => (SPLITTER_CAPACITIES as readonly number[]).includes(v), {
            message: `Capacity must be one of ${SPLITTER_CAPACITIES.join(', ')} outputs`,
          }),
        parentIndex: z.number().int().min(0).nullable(),
      }),
    )
    .min(1)
    .max(CASCADE_MAX_NODES),
});
export type CascadeChainRequest = z.infer<typeof cascadeChainSchema>;

/** One row of the apply's honest report — bulk-style, per splitter. */
export interface CascadeChainItem {
  index: number;
  name: string;
  status: 'created' | 'failed' | 'skipped';
  id: string | null;
  error: string | null;
}

export interface CascadeChainResult {
  items: CascadeChainItem[];
  created: number;
  failed: number;
  skipped: number;
}
